import { Injectable } from '@angular/core';
import { isEmptyArray, isEmptyObject, isStringEmpty } from '@common/helpers';
import {
  FARE_RULE_TABLE_ITEM_LABEL,
  FARE_RULES_LABEL,
  PAYMENT_DETAILS,
  POS_COUNTRY_CODE,
  STAY_PERIOD,
} from '@common/helpers/constants/my-booking/my_booking_itinerary.contant';
import { compareSimilarCharacters } from '@common/helpers/my-booking-itinerary/my-booking-itinerary.helper';
import { PassengerType } from '@common/interfaces';
import { PassengerTypeFareCondition } from '@common/interfaces/fare-conditions/fare-conditions-passenger-type.interface';
import { AirlineI18nAll } from '@common/interfaces/master/airline_i18n_all';
import { AirportI18nAll } from '@common/interfaces/master/airport_i18n_all';
import { FFPriorityCodeI18nAll } from '@common/interfaces/master/ff_priority_code_i18n_all';
import { MFareRuleFull } from '@common/interfaces/master/m_fare_rule_full';
import { TaxLangData } from '@common/interfaces/master/tax_lang_all';
import { MasterDataS01P031 } from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import { UnitPriceItem, UnitPrices } from '@common/interfaces/mybooking/order-prices.interface';
import { MasterJsonKeyPrefix } from '@conf/asw-master.config';
import { SupportClass } from '@lib/components/support-class';
import { DialogClickType, MOffice } from '@lib/interfaces';
import { StaticMsgPipe } from '@lib/pipes';
import { DialogDisplayService } from '@lib/services';
import { BehaviorSubject } from 'rxjs';
import { GetBasicReservationInformationResponse } from 'src/sdk-amcmember';
import {
  FareConditionsResponseData,
  FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner,
  GetOrderResponseData,
  GetOrderResponseDataAir,
  GetOrderResponseDataOrderType,
  GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner,
  Traveler,
} from 'src/sdk-servicing';
import { GetMemberInformationResponse } from 'src/sdk-member';
import { IFlyMemberInfo } from 'src/sdk-member';

@Injectable()
export class MyBookingItineraryService extends SupportClass {
  /** Event Function */
  functionList!: { [key: string]: Function };

  // 画面ID
  public transitionSourceFullFunctionPageId: string = '';
  // 機能ID
  public transitionSourceFunctionId: string = '';
  // 遷移元画面ID
  public transitionSourcePageId: string = '';

  private scrollObservable$ = new BehaviorSubject<string>('');
  get scrollObservable() {
    return this.scrollObservable$;
  }
  scrollTo(scrollId: string) {
    this.scrollObservable$.next(scrollId);
  }

  memberInformation$: BehaviorSubject<IFlyMemberInfo | null> = new BehaviorSubject<IFlyMemberInfo | null>(null);

  masterData$ = new BehaviorSubject<MasterDataS01P031>({});

  constructor(private _dialogService: DialogDisplayService, private _staticMsg: StaticMsgPipe) {
    super();
  }

  /**
   * ダイアログを開く
   * @param messageId ダイアログのメッセージID
   * @returns @see DialogClickType
   */
  openDialogPromiseByMsgId(messageId: string): Promise<DialogClickType> {
    return new Promise((resolve) => {
      this._dialogService
        .openDialog({ message: MasterJsonKeyPrefix.DYNAMIC + messageId })
        .buttonClick$.subscribe((result) => resolve(result.clickType));
    });
  }

  /**
   * 名前 出発地 or 到着地
   * @param locationCode 空港コード(3レター) [flight.departure.locationCode or flight.arrival.locationCode]
   * @param locationName DxAPIから返却された発着空港名称 [flight.departure.locationName or flight.arrival.locationName]
   * @returns 空港名称 [M_AIRPORT_I18N.airport_name] or locationName
   * @example getAirportLocationName("HND", "TOKYO INTL HANEDA")
   */
  getAirportLocationName(locationCode: string = '', locationName: string = ''): string {
    return (
      this.masterData$.value.airportI18nAll?.find((item: AirportI18nAll) => item?.airport_code === locationCode)
        ?.airport_name ??
      locationName ??
      ''
    );
  }

  getAirlineFromCarrierList(airlineCode: string = '', airlineName: string = ''): string {
    return (
      this.masterData$.value.internationalCarrierCodes?.find(
        (item: AirlineI18nAll) => item?.airline_code === airlineCode
      )?.airline_name ??
      airlineName ??
      ''
    );
  }

  /**
   * 搭乗者種別
   * @param traveler
   * @returns
   */
  getPassengerTypeLabel(passengerTypeCode?: string): string {
    let label = '';
    switch (passengerTypeCode) {
      /** 当該搭乗者種別="ADT"(大人)の場合、"label.adult"を表示する */
      case PassengerType.ADT:
        label = FARE_RULE_TABLE_ITEM_LABEL.ADULT;
        break;

      /** 当該搭乗者種別="B15"(ヤングアダルト)の場合、"label.youngAdult"を表示する */
      case PassengerType.B15:
        label = FARE_RULE_TABLE_ITEM_LABEL.YOUNG_ADULT;
        break;

      /** 当該搭乗者種別="CHD"((小児)の場合、"label.child"を表示する */
      case PassengerType.CHD:
        label = FARE_RULE_TABLE_ITEM_LABEL.CHILD;
        break;

      /** 当該搭乗者種別="INS"(席あり幼児)の場合、"label.ins"を表示する */
      case PassengerType.INS:
        label = FARE_RULE_TABLE_ITEM_LABEL.INS;
        break;

      /** 当該搭乗者種別="INF"(席なし幼児の順)の場合、"label.infant"を表示する */
      case PassengerType.INF:
        label = FARE_RULE_TABLE_ITEM_LABEL.INFANT;
        break;

      default:
        label = FARE_RULE_TABLE_ITEM_LABEL.NONE;
        break;
    }
    return label ? this._staticMsg.transform(label) : '';
  }

  /**
   * 乗客の種類を数える
   * @param travelers
   * @returns Map<string, number>
   */
  countPassengerTypes(travelers?: Array<Traveler>): Map<string, number> {
    const countMap: Map<string, number> = new Map();
    travelers?.forEach((traveler) => {
      countMap.set(String(traveler?.passengerTypeCode), (countMap.get(String(traveler?.passengerTypeCode)) || 0) + 1);
    });
    return countMap;
  }

  /**
   * PNR情報取得API.data.prices.unitPrices.<当該搭乗者情報.id>
   * @param travelerId
   * @param orderData
   * @returns UnitPriceItem
   */
  getpaymentInformationsByTravelerId(travelerId?: string, orderData?: GetOrderResponseData): UnitPriceItem {
    const unitPrices = (orderData?.prices?.unitPrices as UnitPrices) ?? ({} as UnitPrices);
    return unitPrices?.[String(travelerId)] ?? ({} as UnitPriceItem);
  }

  /**
   * 税名を取得する
   * @param taxCode
   * @returns string
   */
  getTaxName(taxCode?: string): string {
    const tax = this.masterData$.value.taxLangData?.find((item) => String(item?.tax_code).trim() === String(taxCode));
    return tax?.tax_name ?? '';
  }

  /**
   * コードによる税金の取得
   * @param taxCode
   * @returns string
   */
  getTaxByCode(taxCode?: string): TaxLangData {
    return (
      this.masterData$.value.taxLangData?.find((item) => String(item?.tax_code) === String(taxCode)) ??
      ({} as TaxLangData)
    );
  }

  /**
   * チャネル タイプが Nh3 と等しくない
   * @param orderData
   * @returns true/false
   */
  isChannelTypeNotEqualGdsOrNh3(orderData?: GetOrderResponseData): boolean {
    /**
     * 以下のすべての条件に該当しない場合、表示する。
     * 1)PNR情報取得API応答.data.orderType.channelType=""gds"" (GDS PNR)
     *2)PNR情報取得API応答.data.orderType.channelType=""nh3"" (国内代理店作成PNR)
     *3)PNR情報取得API応答.data.orderType.channelDetail=""merchantNotAnaNdc""(マーチャントがANA以外のNDC PNR)
     */
    return (
      orderData?.orderType?.channelType !== GetOrderResponseDataOrderType.ChannelTypeEnum.Gds &&
      orderData?.orderType?.channelType !== GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3 &&
      orderData?.orderType?.channelDetail !== GetOrderResponseDataOrderType.ChannelDetailEnum.MerchantNotAnaNdc
    );
  }

  /**
   * 値を追加する
   * @param listA
   * @param listB
   * @returns Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner>
   * Example addValues([a], [b])
   */
  addValues(listA: any[], listB: any[]): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    const result: GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] = [];

    /** 通貨コードと配列 a の値のマップを作成します。 */
    const aMap: { [currencyCode: string]: number } = {};
    if (listA && listA.length > 0) {
      for (const currency of listA) {
        aMap[String(currency?.currencyCode)] = Number(currency?.value);
      }
    }

    /** 配列 b の値を配列 a の対応する値に加算します。 */
    if (listB && listB.length > 0) {
      for (const currency of listB) {
        if (aMap[String(currency?.currencyCode)]) {
          aMap[String(currency?.currencyCode)] += Number(currency?.value);
        } else {
          aMap[String(currency?.currencyCode)] = Number(currency?.value);
        }
      }
    }

    /** マップを Currency オブジェクトの配列に変換し直します。*/
    if (aMap) {
      for (const currencyCode in aMap) {
        result.push({
          value: aMap[currencyCode],
          currencyCode: currencyCode,
        });
      }
    }

    return result;
  }

  /** (1.2.5) 支払総額ラベル */
  getPaymentTotalLabel(orderData?: GetOrderResponseData): string {
    /**
     * 2.4)PNR情報取得API応答.data.orderType.channelDetail="merchantAnaNdc"(マーチャントがANAのNDC PNR)、かつPNR情報取得API応答.data.travelDocuments.prices.currencyCodeと、オフィス情報.通貨コードが同一でない
     * ※「イベントID(1)」で取得したオフィス情報からオフィス情報.通貨コードを取得する。
     */
    const isMerchantAnaNdc =
      orderData?.orderType?.channelDetail === GetOrderResponseDataOrderType.ChannelDetailEnum.MerchantAnaNdc;
    const mOffice = this.masterData$.value.mOffice?.[0] ?? ({} as MOffice);
    const isCurrencyCodeDifferent = orderData?.travelDocuments?.some(
      (travelDocument) => String(travelDocument?.price?.currencyCode) !== String(mOffice?.currency_code)
    );

    /** 以下の条件に従い、支払総額に対するラベルを表示する。 */
    /** 1)PNR情報取得API応答.data.orderStatus = "unpurchased" (未購入)、かつPNR情報取得API応答.data.serviceSummary.hasRequested.keepMyFare = trueの場合、未決済総額の旨を表示する。*/
    if (
      orderData?.orderStatus === GetOrderResponseData.OrderStatusEnum.Unpurchased &&
      Boolean(orderData?.serviceSummary?.hasRequested?.keepMyFare) === true
    ) {
      return PAYMENT_DETAILS.PAYMENT_TOTAL_AMOUNT_NOT_PURCHASED;

      /**
       * 2)以下のいずれの条件に該当する場合、Ancillaryサービス総額の旨を表示する。
       * 2.1)PNR情報取得API応答.data.orderType.channelType="gds" (GDS PNR)
       * 2.2)PNR情報取得API応答.data.orderType.channelType="nh3"(国内代理店作成PNR)
       * 2.3)PNR情報取得API応答.data.orderType.channelDetail="merchantNotAnaNdc"(マーチャントがANA以外のNDC PNR)
       */
    } else if (
      orderData?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Gds ||
      orderData?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3 ||
      orderData?.orderType?.channelDetail === GetOrderResponseDataOrderType.ChannelDetailEnum.MerchantNotAnaNdc ||
      (isMerchantAnaNdc && isCurrencyCodeDifferent)
    ) {
      return PAYMENT_DETAILS.PAYMENT_PAID_SERVICES;
    } else {
      /** 3)上記以外の場合、支払総額の旨を表示する。*/
      return PAYMENT_DETAILS.ITINERARY_AMOUNT_DUE;
    }
  }

  /** [1.2.6] 支払総額 / [1.9.6] 支払総額 */
  getTotalPrices(orderData?: GetOrderResponseData): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    let totalPaymentAmount: Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> = [];

    /**
     * 1)以下のいずれの条件に該当する場合、currencyCodeに基づいて、支払総額[]=PNR情報取得API応答.data.prices.totalPrices.servicePrices.total(サービス税抜総額、サービス税金総額の合計金額)とする。
     * 1.1)PNR情報取得API応答.data.orderType.channelType="gds" (GDS PNR)
     * 1.2)PNR情報取得API応答.data.orderType.channelType="nh3" (国内代理店作成PNR)
     * 1.3)PNR情報取得API応答.data.orderType.channelDetail="merchantNotAnaNdc"(マーチャントがANA以外のNDC PNR)
     */
    if (
      orderData?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Gds ||
      orderData?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3 ||
      orderData?.orderType?.channelDetail === GetOrderResponseDataOrderType.ChannelDetailEnum.MerchantNotAnaNdc
    ) {
      totalPaymentAmount =
        orderData?.prices?.totalPrices?.servicePrices?.total ??
        ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[]);

      /** 2)上記以外の場合、currencyCodeに基づいて、支払総額[]=PNR情報取得API応答.data.prices.totalPrices.total(航空運賃、税金、発券手数料、有料サービス金額の合計)とする。*/
    } else {
      totalPaymentAmount =
        orderData?.prices?.totalPrices?.total ?? ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[]);
    }

    return totalPaymentAmount as Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner>;
  }

  /**
   * [1.2.14]/[1.9.15] 税金総額 (航空券金額情報)
   * @param pnr
   * @returns array
   */
  getTaxTotalPrices(
    pnr?: GetOrderResponseData,
    mOffice?: MOffice
  ): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    let totalTaxAmount: Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> = [];

    if (Boolean(pnr?.orderType?.isAwardBooking) === false && mOffice?.pos_country_code !== POS_COUNTRY_CODE.CA) {
      /**
       * 1)PNR情報取得API応答.data.orderType.isAwardBooking = false (有償PNR)、かつPOS国コード ≠ "CA" (Canada)の場合、
       * 1.1)currencyCodeに基づいて、税金総額[] = PNR情報取得API応答.data.prices.totalPrices.ticketPrices.totalTaxes[]
       * 1.2)<以下、税金総額[]の要素数の繰り返し> (それぞれの要素が当該税金総額)
       * 当該税金総額.currencyCodeを通貨コード、当該税金総額.valueを金額として、表示する。
       * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
       * 通貨コードは改行で区切る。
       * <ここまで、支払総額[]の要素数の繰り返し>
       */
      totalTaxAmount = pnr?.prices?.totalPrices?.ticketPrices?.totalTaxes ?? [];
    } else if (Boolean(pnr?.orderType?.isAwardBooking) === false && mOffice?.pos_country_code === POS_COUNTRY_CODE.CA) {
      /**
       * 2)PNR情報取得API応答.data.orderType.isAwardBooking = false (有償PNR)、かつPOS国コード = "CA" (Canada)の場合、
       * 2.1)currencyCodeに基づいて、税金総額[] = PNR情報取得API応答.data.prices.totalPrices.ticketPrices.thirdPartyCharges[]。
       * 2.2)<以下、税金総額[]の要素数の繰り返し> (それぞれの要素が当該税金総額)
       * 当該税金総額.currencyCodeを通貨コード、当該税金総額.valueを金額として、表示する。
       * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
       * 通貨コードは改行で区切る。
       * <ここまで、支払総額[]の要素数の繰り返し>
       */
      totalTaxAmount = pnr?.prices?.totalPrices?.ticketPrices?.thirdPartyCharges ?? [];
    } else if (Boolean(pnr?.orderType?.isAwardBooking) === true) {
      /**
       * 3)PNR情報取得API応答.data.orderType.isAwardBooking = true (特典PNR)の場合、
       * 3.1)currencyCodeに基づいて、税金総額[] = PNR情報取得API応答.data.prices.totalPrices.ticketPrices.totalTaxes[] + PNR情報取得API応答.data.prices.totalPrices.ticketPrices.flightSurcharges[]。
       * 3.2)<以下、税金総額[]の要素数の繰り返し> (それぞれの要素が当該税金総額)
       * 当該税金総額.currencyCodeを通貨コード、当該税金総額.valueを金額として、表示する。
       * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
       * 通貨コードは改行で区切る。
       * <ここまで、支払総額[]の要素数の繰り返し>
       * ※0でも表示する。税金が免除される場合も0での表示となる。
       */
      totalTaxAmount = this.addValues(
        pnr?.prices?.totalPrices?.ticketPrices?.totalTaxes ?? [],
        pnr?.prices?.totalPrices?.ticketPrices?.flightSurcharges ?? []
      );
    }

    return totalTaxAmount;
  }

  /**
   * [1.2.14]/[1.9.15] 税金総額 (航空券金額情報)
   * @param currentOffice
   * @returns string
   */
  getLabelTaxTotalPrices(currentOffice: MOffice): string {
    /** ※項目名について、POS国コード ≠ "CA" (Canada)の場合は税金総額、POS国コード="CA"(Canada)の場合は各国諸税・空港使用料等総額とする。 */
    return currentOffice?.pos_country_code === POS_COUNTRY_CODE.CA ? 'lable.taxesFeeArges' : 'label.taxFare2';
  }

  /** [1.9.10] 運賃詳細リンク */
  getFFCodePrefixs(fareConditions?: FareConditionsResponseData): Array<MFareRuleFull> {
    /** 1.1)各区間のFFコードプレフィックスを格納するため、空のリストを作成する。*/
    let ffCodeList: Array<MFareRuleFull> = [];

    const airOfferConditionBounds =
      fareConditions?.airOfferConditions?.airOfferConditionBounds ??
      ([] as Array<FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner>);

    for (const airOfferConditionBound of airOfferConditionBounds) {
      /** 1.2)運賃フルルール情報より、Fare Familyコードプレフィックスの値のみをリストとして取得し保持する。*/
      for (const fareRuleFull of this.masterData$.value.mFareRuleFull ?? []) {
        /** 1.3)全プレフィックスリストから、当該全プレフィックスの先頭n文字=当該運賃ルール用のバウンド毎.fareFamilyCodeの先頭n文字となる全プレフィックスリスト(以下、合致したプレフィックスリスト)を抽出する。*/
        /** 1.4)合致したプレフィックスリストが1件の場合、区間毎FFコードプレフィックスリストに合致したプレフィックスリスト[0]を追加し、当繰り返しを中断する。*/
        if (
          compareSimilarCharacters(
            fareRuleFull?.fare_family_code_prefix,
            String(airOfferConditionBound?.fareFamilyCode)
          )
        ) {
          /** 1.5)区間毎FFコードプレフィックスリストに空要素を追加し、次の区間の繰り返し処理に移る。*/
          ffCodeList.unshift(fareRuleFull);
          break;
        }
      }
    }
    return ffCodeList;
  }

  /** 優先コードを取得する */
  getFFPriorityCode(priorityCode?: string): FFPriorityCodeI18nAll {
    return (
      this.masterData$.value.fFPriorityCodeAll?.find(
        (ffPriorityCode: FFPriorityCodeI18nAll) => ffPriorityCode?.ff_priority_code === priorityCode
      ) ?? ({} as FFPriorityCodeI18nAll)
    );
  }

  /** [1.5.12]/[1.10.10] プロモーション (This function is used for the above 2 items) */
  isShowPromotion(orderData?: GetOrderResponseData): boolean {
    /**
     * 1)「イベントID(1)」で取得したオフィス情報からプロモーション利用可能フラグを取得する。
     * 2)PNR情報取得API応答.data.orderType.isAwardBooking=false(有償PNR)、かつプロモーション利用可能フラグ= true(プロモーションコード対象)の場合、以下の条件で表示する。
     */
    const mOffice = this.masterData$.value.mOffice?.[0] ?? ({} as MOffice);
    return (
      Boolean(orderData?.orderType?.isAwardBooking) === false && Boolean(mOffice?.promotion_available_flag) === true
    );
  }

  /**
   * [1.2.18]/[1.9.77] Keep My Fare支払税金総額
   * @param orderData
   * @returns true/false
   */
  isKeepMyFareTaxTotal(orderData?: GetOrderResponseData): boolean {
    /** PNR情報取得API応答.data.orderStatus = "unpurchased" (未決済)、かつPNR情報取得API応答.data.prices.totalPrices.keepMyFarePrices.totalTaxesが存在する場合 */
    return Boolean(
      orderData?.orderStatus === GetOrderResponseData.OrderStatusEnum.Unpurchased &&
        !isEmptyArray(orderData?.prices?.totalPrices?.keepMyFarePrices?.totalTaxes)
    );
  }

  /**
   * [1.2.18]/[1.9.77] Keep My Fare支払税金総額
   * @param orderData
   * @returns array
   */
  keepMyFarePricesTotalTaxes(
    orderData?: GetOrderResponseData
  ): Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner> {
    /**
     * <以下、PNR情報取得API応答.data.prices.totalPrices.keepMyFarePrices.totalTaxes[]の要素数の繰り返し> (それぞれの要素が当該Keep My Fare税額情報)
     * 当該Keep My Fare税額情報.currencyCodeを通貨コード、当該Keep My Fare税額情報.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、PNR情報取得API応答.data.prices.totalPrices.keepMyFarePrices.totalTaxes[]の要素数の繰り返し>
     */
    return (
      orderData?.prices?.totalPrices?.keepMyFarePrices?.totalTaxes ??
      ([] as Array<GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner>)
    );
  }

  /**
   * [1.5.10]/[1.10.8] FF名称
   * @param airOfferConditionBound
   * @param orderData
   * @returns string
   */
  getFFName(
    airOfferConditionBound?: FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner,
    orderData?: GetOrderResponseData
  ): string {
    let ffName: string = '';
    /**
     * ただし、PNR情報取得API応答.data.orderType.channelDetail ≠ "asw" (ASW作成PNR)、
     * かつPNR情報取得API応答.data.orderStatus = "unticketed"、
     * かつPNR情報取得API応答.data.air.tripType="international"(国際旅程)の場合、利用運賃が表示できない旨を表示する
     */
    if (
      orderData?.orderType?.channelDetail !== GetOrderResponseDataOrderType.ChannelDetailEnum.Asw &&
      orderData?.orderStatus === GetOrderResponseData.OrderStatusEnum.Unticketed &&
      orderData?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.International
    ) {
      ffName = this._staticMsg.transform(FARE_RULE_TABLE_ITEM_LABEL.HYPHEN);
    } else if (!orderData?.orderType?.isAwardBooking) {
      /**
       * 1) PNR情報取得API応答.data.orderType.isAwardBooking = false(有償PNR)の場合、
       * 当該運賃ルール用のバウンド.priorityCodeを基に「イベントID(1)」で取得したFFPriority(国際化)リストをフィルタして、Fare Family名称を取得する。
       */
      /**
       * 1.1)Fare Family名称が存在する場合、Fare Family名称を表示する。
       * 1.2)取得できなかった、または当該運賃ルール用のバウンド.fareFamilyCodeが存在しない場合は非表示とする。
       */
      const ffNameObj = this.masterData$.value.fFPriorityCodeAll?.find(
        (item) => item.ff_priority_code === airOfferConditionBound?.priorityCode
      );
      ffName = ffNameObj?.ff_name ?? '';
    }
    return ffName;
  }

  /**
   * [1.5.9]/[1.10.24] FF名称ラベル
   * @param isAwardBooking
   * @returns string
   */
  getFFNameLabel(isAwardBooking?: boolean): string {
    /**
     * 1)PNR情報取得API応答.data.orderType.isAwardBooking=false(有償PNR)の場合、"label.fareName"(利用運賃)を表示する
     * 2)PNR情報取得API応答.data.orderType.isAwardBooking=true(特典PNR)の場合、"label.applidRule"(適用規則)を表示する。
     */
    if (isAwardBooking) {
      return FARE_RULE_TABLE_ITEM_LABEL.APPLID_RULE;
    }
    return FARE_RULE_TABLE_ITEM_LABEL.FARE_NAME;
  }

  /** [1.5.1] 運賃ルール / [1.10.1] 運賃ルール詳細モーダ */
  showContentModalFareRules(orderData?: GetOrderResponseData, fareCondition?: FareConditionsResponseData): boolean {
    /**
     * 運賃ルール・手荷物情報取得API応答.dataが存在する、
     * かつ(PNR情報取得API応答.data.orderType.channelType="nh"(直営PNR)、
     * または"ndc"(NDC PNR))の場合、運賃ルール詳細モーダルを表示する。
     */
    return (
      !isEmptyObject(fareCondition) &&
      (orderData?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Nh ||
        orderData?.orderType?.channelType === GetOrderResponseDataOrderType.ChannelTypeEnum.Ndc)
    );
  }

  openFareRuleOnNewTab(isAwardBooking: boolean, fareConditions: FareConditionsResponseData) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = this.linkContentLabel(isAwardBooking, fareConditions);

    const linkElement = tempDiv.querySelector('a');
    if (linkElement?.href && linkElement?.target) {
      window.open(linkElement?.href, linkElement?.target);
    }
  }

  /**
   * [1.5.2] 運賃ルール詳細モーダル表示
   * ※PNR情報取得API応答.data.orderType.isAwardBooking = true(特典PNR)の場合、icon_external_primary_16(別タブアイコン)を併記する
   */
  linkContentLabel(isAwardBooking: boolean, fareConditions: FareConditionsResponseData): string {
    let label: string = '';
    /** 1)PNR情報取得API応答.data.orderType.isAwardBooking = false(有償PNR)の場合、"reader.itineraryFareRulesDetail"(運賃ルール詳細表示ボタン)を表示する */
    if (!isAwardBooking) {
      label = FARE_RULE_TABLE_ITEM_LABEL.ITINERARY_FARE_RULES_DETAIL;

      /** 2)PNR情報取得API応答.data.orderType.isAwardBooking = true(特典PNR)の場合、特典種別を基に、以下条件に従いコンテンツリンクとして表示する */
      /** 提携: 2)PNR情報取得API応答.data.orderType.isAwardBooking = true(特典PNR)の場合、特典用の運賃ルール詳細表示ボタンを表示する */
    } else {
      let airOfferConditionBounds = fareConditions?.airOfferConditions?.airOfferConditionBounds ?? [];
      if (!isEmptyArray(airOfferConditionBounds)) {
        switch (airOfferConditionBounds[0].awardType) {
          /** 2.3)運賃ルール・手荷物情報取得API応答.data.airOfferConditions.airOfferConditionBounds[0].awardType="dom"(国内特典)の場合、国内特典である旨 */
          case FareConditionsResponseDataAirOfferConditionsAirOfferConditionBoundsInner.AwardTypeEnum.Dom:
            label = FARE_RULES_LABEL.DOMESTIC_AWARD;
            break;
        }
      }
    }
    return label ? this._staticMsg.transform(label) : '';
  }

  /**
   * [1.2.10]/[1.9.11] 航空券金額情報
   * @param orderData
   * @returns true/false
   */
  showTicketTotalPrices(orderData?: GetOrderResponseData): boolean {
    /**
     * 以下のすべての条件に該当しない場合、表示する。
     * 1)PNR情報取得API応答.data.orderType.channelType="gds" (GDS PNR)
     * 2)PNR情報取得API応答.data.orderType.channelType="nh3" (国内代理店作成PNR)
     * 3)PNR情報取得API応答.data.orderType.channelDetail="merchantNotAnaNdc"(マーチャントがANA以外のNDC PNR)
     */
    return Boolean(
      orderData?.orderType?.channelType !== GetOrderResponseDataOrderType.ChannelTypeEnum.Gds &&
        orderData?.orderType?.channelType !== GetOrderResponseDataOrderType.ChannelTypeEnum.Nh3 &&
        orderData?.orderType?.channelDetail !== GetOrderResponseDataOrderType.ChannelDetailEnum.MerchantNotAnaNdc
    );
  }

  /**
   * [1.2.11]/[1.9.12] 運賃総額
   * @param orderData
   * @returns array
   */
  getServicePricesBase(
    orderData?: GetOrderResponseData
  ): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    /**
     * <以下、PNR情報取得API応答.data.prices.totalPrices.ticketPrices.base[]の要素数の繰り返し> (それぞれの要素が当該航空券総額)
     * 当該航空券総額.currencyCodeを通貨コード、当該航空券総額.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、PNR情報取得API応答.data.prices.totalPrices.ticketPrices.base[]の要素数の繰り返し>
     */
    return (
      orderData?.prices?.totalPrices?.ticketPrices?.base ??
      ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[])
    );
  }

  /**
   * [1.2.11]/[1.9.12] 運賃総額
   * @param orderData
   * @returns true/false
   */
  showFareTotalPrices(orderData?: GetOrderResponseData): boolean {
    /** PNR情報取得API応答.data.prices.totalPrices.ticketPrices.base[]が存在する場合 */
    return !isEmptyArray(orderData?.prices?.totalPrices?.ticketPrices?.base);
  }

  /**
   * [1.2.8]/[1.9.9] Keep My Fare支払総額
   * @param orderData
   * @returns true/false
   */
  showKeepMyFareTotalPrices(orderData?: GetOrderResponseData): boolean {
    /** PNR情報取得API応答.data.orderStatus = "unpurchased" (未決済)、かつPNR情報取得API応答.data.prices.totalPrices.keepMyFarePrices.baseが存在する場合 */
    return Boolean(
      orderData?.orderStatus === GetOrderResponseData.OrderStatusEnum.Unpurchased &&
        !isEmptyArray(orderData?.prices?.totalPrices?.keepMyFarePrices?.base)
    );
  }

  /**
   * [1.2.8]/[1.9.9] Keep My Fare支払総額
   * @param orderData
   * @returns array
   */
  farePriceBase(orderData?: GetOrderResponseData): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    return (
      orderData?.prices?.totalPrices?.keepMyFarePrices?.base ??
      ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[])
    );
  }

  /**
   * [1.2.9]/[1.9.10] Bid My Priceアップグレード料金総額
   * @param orderData
   * @returns true/false
   */
  showBidMyPriceFee(orderData?: GetOrderResponseData): boolean {
    /** PNR情報取得API応答.data.prices.totalPrices.bidMyPriceTotalPriceが存在する場合 */
    return !isEmptyArray(orderData?.prices?.totalPrices?.bidMyPriceTotalPrice);
  }

  /**
   * [1.2.9]/[1.9.10] Bid My Priceアップグレード料金総額
   * @param orderData
   * @returns array
   */
  bidMyPriceTotalPrice(
    orderData?: GetOrderResponseData
  ): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    /**
     * <以下、PNR情報取得API応答.data.prices.totalPrices.bidMyPriceTotalPrice[]の要素数繰り返し>(それぞれの要素が当該Bid My Priceアップグレード料金総額情報)
     * 当該Bid My Priceアップグレード料金総額情報.currencyCodeを通貨コード、当該Bid My Priceアップグレード料金総額情報.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、PNR情報取得API応答.data.prices.totalPrices.bidMyPriceTotalPrice[]の要素数繰り返し>
     */
    return (
      orderData?.prices?.totalPrices?.bidMyPriceTotalPrice ??
      ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[])
    );
  }

  /**
   * [1.2.12]/[1.9.13] フライトサーチャージ (航空券金額情報)
   * @param orderData
   * @returns true/false
   */
  showFlightSurchargeTotalPrices(orderData?: GetOrderResponseData): boolean {
    /** PNR情報取得API応答.data.prices.totalPrices.ticketPrices.flightSurcharges[]が存在する場合 */
    return !isEmptyArray(orderData?.prices?.totalPrices?.ticketPrices?.flightSurcharges);
  }

  /**
   * [1.2.12]/[1.9.13] フライトサーチャージ (航空券金額情報)
   * @param orderData
   * @returns array
   */
  flightSurcharges(orderData?: GetOrderResponseData): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    /**
     * <以下、PNR情報取得API応答.data.prices.totalPrices.ticketPrices.flightSurcharges[]の要素数の繰り返し> (それぞれの要素が当該フライトサーチャージ情報)
     * 当該フライトサーチャージ情報.currencyCodeを通貨コード、当該フライトサーチャージ情報.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、PNR情報取得API応答.data.prices.totalPrices.ticketPrices.flightSurcharges[]の要素数の繰り返し>
     */
    return (
      orderData?.prices?.totalPrices?.ticketPrices?.flightSurcharges ??
      ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[])
    );
  }

  /**
   * [1.2.13]/[1.9.14] 燃油特別付加運賃等総額 (航空券金額情報)
   * @param orderData
   * @param mOffice
   * @returns true/false
   */
  showSpecialFuelSurchargeTotalPrices(orderData?: GetOrderResponseData, mOffice?: MOffice): boolean {
    /**
     * ※以下の処理に記載されているPOS国コードがオフィス情報「イベントID(1)」から取得される。
     * PNR情報取得API応答.data.orderType.isAwardBooking = false (有償PNR)、かつPOS国コード = "CA" (Canada)、かつPNR情報取得API応答.data.prices.totalPrices.ticketPrices.airTransportationCharges[]が存在する場合
     */
    return (
      Boolean(orderData?.orderType?.isAwardBooking) === false &&
      mOffice?.pos_country_code === POS_COUNTRY_CODE.CA &&
      !isEmptyArray(orderData?.prices?.totalPrices?.ticketPrices?.airTransportationCharges)
    );
  }

  /**
   * [1.2.13]/[1.9.14] 燃油特別付加運賃等総額 (航空券金額情報)
   * @param orderData
   * @returns array
   */
  airTransportationCharges(
    orderData?: GetOrderResponseData
  ): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    /**
     * <以下、PNR情報取得API応答.data.prices.totalPrices.ticketPrices.airTransportationCharges[]の要素数の繰り返し> (それぞれの要素が当該合計値情報)
     * 当該合計値情報.currencyCodeを通貨コード、当該合計値情報.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、PNR情報取得API応答.data.prices.totalPrices.ticketPrices.airTransportationCharges[]の要素数の繰り返し>
     */
    return (
      orderData?.prices?.totalPrices?.ticketPrices?.airTransportationCharges ??
      ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[])
    );
  }

  /**
   * [1.2.15]/[1.9.16] 発券手数料、交換発行手数料総 (航空券金額情報)
   * @param mOffice
   * @param ticketPricesTotalFee
   * @returns true/false
   */
  showTicketFeeTotalPrices(mOffice?: MOffice, orderData?: GetOrderResponseData): boolean {
    /** POS国コード ≠ "CA" (Canada)、かつPNR情報取得API応答.data.prices.totalPrices.ticketPrices.totalFee[]が存在する場合 */
    return (
      String(mOffice?.pos_country_code) !== POS_COUNTRY_CODE.CA &&
      !isEmptyArray(orderData?.prices?.totalPrices?.ticketPrices?.totalFee)
    );
  }

  /**
   * [1.2.15]/[1.9.16] 発券手数料、交換発行手数料総 (航空券金額情報)
   * @param mOffice
   * @param ticketPricesTotalFee
   * @returns true/false
   */
  ticketPricesTotalFee(
    orderData?: GetOrderResponseData
  ): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    /**
     * <以下、PNR情報取得API応答.data.prices.totalPrices.ticketPrices.totalFee[]の要素数の繰り返し> (それぞれの要素が当該発券手数料・交換発行手数料)
     * 当該発券手数料・交換発行手数料.currencyCodeを通貨コード、当該発券手数料・交換発行手数料.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、PNR情報取得API応答.data.prices.totalPrices.ticketPrices.totalFee[]の要素数の繰り返し>
     * ※0でも表示する。
     */
    return (
      orderData?.prices?.totalPrices?.ticketPrices?.totalFee ??
      ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[])
    );
  }

  /**
   * [1.2.16]/[1.9.17] Ancillaryサービス税抜総額
   * @param orderData
   * @returns true/false
   */
  showAncillaryServiceTotalPrices(orderData?: GetOrderResponseData): boolean {
    /** PNR情報取得API応答.data.prices.totalPrices.servicePrices.base[]が存在する場合 */
    return !isEmptyArray(orderData?.prices?.totalPrices?.servicePrices?.base);
  }

  /**
   * [1.2.16]/[1.9.17] Ancillaryサービス税抜総額
   * @param orderData
   * @returns array
   */
  servicePricesBase(orderData?: GetOrderResponseData): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    /**
     * <以下、PNR情報取得API応答.data.prices.totalPrices.servicePrices.base[]の要素数の繰り返し> (それぞれの要素が当該サービス税抜総額情報)
     * 当該サービス税抜総額情報.currencyCodeを通貨コード、当該サービス税抜総額情報.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、PNR情報取得API応答.data.prices.totalPrices.servicePrices.base[]の要素数の繰り返し>
     */
    return (
      orderData?.prices?.totalPrices?.servicePrices?.base ??
      ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[])
    );
  }

  /**
   * [1.2.17]/[1.9.18] Ancillaryサービス税金総額
   * @param orderData
   * @returns true/false
   */
  showAncillaryServiceTaxTotalPrices(orderData?: GetOrderResponseData): boolean {
    /** PNR情報取得API応答.data.prices.totalPrices.servicePrices.totalTaxes[]が存在する場合 */
    return !isEmptyArray(orderData?.prices?.totalPrices?.servicePrices?.totalTaxes);
  }

  /**
   * [1.2.17]/[1.9.18] Ancillaryサービス税金総額
   * @param orderData
   * @returns array
   */
  servicePricesTotal(orderData?: GetOrderResponseData): GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[] {
    /**
     * <以下、PNR情報取得API応答.data.prices.totalPrices.servicePrices.totalTaxes[]の要素数の繰り返し> (それぞれの要素が当該サービス税金総額情報)
     * 当該サービス税金総額情報.currencyCodeを通貨コード、当該サービス税金総額情報.valueを金額として、表示する。
     * フォーマット:共通部品(G03-016)の金額フォーマット処理を参照
     * 通貨コードは改行で区切る。
     * <ここまで、PNR情報取得API応答.data.prices.totalPrices.servicePrices.totalTaxes[]の要素数の繰り返し>
     */
    return (
      orderData?.prices?.totalPrices?.servicePrices?.totalTaxes ??
      ([] as GetOrderResponseDataPricesTotalPricesTicketPricesTotalInner[])
    );
  }

  destroy(): void {}
}
